import { useMemo, useState } from 'react';
import { canonicalPlatformResourceId, labelForPlatformKey } from '../../constants/platformPermissions';
import { formatPlatformPermissionDetails } from './PlatformPermissionsPanel';

function typeRank(type) {
  if (type === 'groups_periods') return 0;
  if (type === 'groups') return 1;
  if (type === 'user_groups_periods') return 2;
  if (type === 'users') return 3;
  return 9;
}

function typeLabel(type) {
  if (type === 'groups_periods') return 'GP';
  if (type === 'groups') return 'Group';
  if (type === 'user_groups_periods') return 'UGP';
  if (type === 'users') return 'User';
  return type || '?';
}

function gpLabel(gp) {
  if (!gp) return null;
  const period = gp.period_title || gp.period_code || gp.period_id || '';
  return period ? `${gp.group_name || '?'} — ${period}` : gp.group_name || '?';
}

function ugpLabel(ugp) {
  if (!ugp) return null;
  const name =
    `${ugp.first_name || ''} ${ugp.last_name || ''}`.trim() ||
    ugp.email ||
    ugp.user_email ||
    ugp.user_id ||
    '?';
  const team = gpLabel({
    group_name: ugp.group_name,
    period_title: ugp.period_title,
    period_code: ugp.period_code,
    period_id: ugp.groups_periods_id
  });
  return `${name} • ${team}`;
}

/**
 * Invert live platform permission rows: each GP / group / UGP lists the keys granted to it.
 * Dev inspector for drafting role templates from what UIS actually has.
 */
export default function GrantsByGranteePanel({
  productId,
  permissions,
  groupsPeriods = [],
  durableGroups = [],
  userGroupsPeriods = [],
  onOpenPermission
}) {
  const [query, setQuery] = useState('');

  const gpById = useMemo(
    () => new Map((groupsPeriods || []).map((gp) => [String(gp.id), gp])),
    [groupsPeriods]
  );
  const groupById = useMemo(
    () => new Map((durableGroups || []).map((g) => [String(g.id), g])),
    [durableGroups]
  );
  const ugpById = useMemo(
    () => new Map((userGroupsPeriods || []).map((u) => [String(u.id), u])),
    [userGroupsPeriods]
  );

  const buckets = useMemo(() => {
    const map = new Map();
    const add = (type, id, label) => {
      const key = `${type}:${id}`;
      if (!map.has(key)) {
        map.set(key, { key, type, id, label, perms: [] });
      }
      return map.get(key);
    };

    for (const perm of permissions || []) {
      const type = String(perm.grantee_type || '');
      const ids = Array.isArray(perm.grantees) ? perm.grantees.map(String) : [];
      if (!ids.length) {
        add(type || 'unknown', '(none)', 'No grantees').perms.push(perm);
        continue;
      }
      for (const id of ids) {
        let label = id;
        if (id === '*all*') label = `*all* (${type || 'grantees'})`;
        else if (id === '*global*') label = 'Global';
        else if (type === 'groups_periods') label = gpLabel(gpById.get(id)) || id;
        else if (type === 'groups') {
          const g = groupById.get(id);
          label = g ? `${g.name || '?'}${g.group_type ? ` (${g.group_type})` : ''}` : id;
        } else if (type === 'user_groups_periods') label = ugpLabel(ugpById.get(id)) || id;
        add(type, id, label).perms.push(perm);
      }
    }

    return [...map.values()].sort((a, b) => {
      const tr = typeRank(a.type) - typeRank(b.type);
      if (tr) return tr;
      return String(a.label).localeCompare(String(b.label), undefined, { sensitivity: 'base' });
    });
  }, [permissions, gpById, groupById, ugpById]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return buckets;
    return buckets.filter((bucket) => {
      if (bucket.label.toLowerCase().includes(q) || bucket.id.toLowerCase().includes(q)) {
        return true;
      }
      return bucket.perms.some((perm) => {
        const key = String(perm.resource_id || perm.permission_name || '').toLowerCase();
        return key.includes(q);
      });
    });
  }, [buckets, query]);

  return (
    <div className="ppm-grants-by-grantee">
      <div className="ppm-grants-by-grantee-toolbar">
        <p className="ppm-grants-by-grantee-intro">
          Live UIS rows for <code>platform={productId}</code>, inverted by grantee. Use this to
          see what a Teacher / Admin team actually holds before editing Learn role defaults.
        </p>
        <label className="ppm-grants-by-grantee-search">
          <span className="ppm-sr">Filter teams or keys</span>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter team, person, or permission key"
          />
        </label>
      </div>
      {filtered.length === 0 ? (
        <p className="ppm-grants-by-grantee-empty">No grantees match.</p>
      ) : (
        <ul className="ppm-grants-by-grantee-list">
          {filtered.map((bucket) => (
            <li key={bucket.key} className="ppm-grants-by-grantee-bucket">
              <header className="ppm-grants-by-grantee-head">
                <span className="ppm-grants-by-grantee-scope" title={bucket.type}>
                  {typeLabel(bucket.type)}
                </span>
                <div className="ppm-grants-by-grantee-who">
                  <strong>{bucket.label}</strong>
                  <code>{bucket.id}</code>
                </div>
                <span className="ppm-grants-by-grantee-count">
                  {bucket.perms.length} key{bucket.perms.length === 1 ? '' : 's'}
                </span>
              </header>
              <ul className="ppm-grants-by-grantee-keys">
                {bucket.perms.map((perm) => {
                  const key =
                    canonicalPlatformResourceId(
                      productId,
                      perm.permission_type,
                      perm.resource_id
                    ) ||
                    perm.permission_name ||
                    perm.id;
                  const details =
                    formatPlatformPermissionDetails(productId, perm) ||
                    labelForPlatformKey(productId, perm.permission_type, perm.resource_id) ||
                    key;
                  return (
                    <li key={`${bucket.key}:${perm.id}`}>
                      <button
                        type="button"
                        className="ppm-grants-by-grantee-key"
                        onClick={() => onOpenPermission?.(perm)}
                        title="Open this grant"
                      >
                        <span className="ppm-grants-by-grantee-key-id">{key}</span>
                        <span className="ppm-grants-by-grantee-key-details">{details}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
